import React from 'react';
import styled from 'styled-components';
import { Avatar as AntdAvatar } from 'antd';
import { AuthContext, AuthContextType } from 'contexts';
import { buildCssProps } from 'utils';
import { Color } from '@bb/elements-web';
import { Icon, IconNameType } from '../Icon';
import theme, { ColorNameType } from '../theme';

const API_KEY = 'AVsLFCmooSeCfsjDRz3Cbz';
const BASE_URL = 'https://cdn.filestackcontent.com';

export type AvatarStyleProps = {
  defaultColor?: ColorNameType;
  color?: ColorNameType;
  backgroundColor?: ColorNameType;
};

export type AvatarProps = {
  width?: number;
  useAuthUser?: boolean;
  user?: {
    firstName?: string;
    lastName?: string;
    avatarFilestackId?: string;
  };
  className?: string;
  src?: string;
  defaultType?: IconNameType;
} & AvatarStyleProps;

export const Avatar: React.FC<AvatarProps> = ({
  useAuthUser,
  user,
  width = 32,
  className = '',
  src,
  defaultType = 'antdUserOutlined',
  defaultColor = 'white',
  color,
  backgroundColor,
}) => {
  const { auth } = React.useContext<AuthContextType>(AuthContext);
  const userToDisplay = useAuthUser ? auth : user;

  if (!userToDisplay && !src) {
    return (
      <EmptyAvatar
        $width={width}
        className={className}
        icon={<Icon type={defaultType} color={defaultColor} />}
      />
    );
  }

  const getInitials = () => {
    if (!userToDisplay) return '';
    const { firstName = '', lastName = '' } = userToDisplay;
    const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`;
    return initials.toUpperCase();
  };

  const getImage = () => {
    if (src) return src;

    const { avatarFilestackId } = userToDisplay ?? {};
    if (!avatarFilestackId) return '';

    const imageSizes = {
      medium: 75,
      small: 32,
    };

    let sizeNumber = imageSizes.small;
    if (width > imageSizes.small) sizeNumber = imageSizes.medium;

    const resize = `resize=width:${sizeNumber},height:${sizeNumber}`;

    return `${BASE_URL}/${API_KEY}/${resize}/${avatarFilestackId}`;
  };

  return (
    <StyledAvatar
      $width={width}
      $color={color}
      $backgroundColor={backgroundColor}
      className={className}
      src={getImage()}
      $setDefaultInitials={!(src || color || backgroundColor)}
      $isAuthUser={useAuthUser}
    >
      {getInitials()}
    </StyledAvatar>
  );
};

const StyledAvatar = styled(AntdAvatar)<{
  $width: number;
  $color?: ColorNameType;
  $backgroundColor?: ColorNameType;
  $setDefaultInitials?: boolean;
  $isAuthUser?: boolean;
}>`
  font-weight: 600; // Semi Bold
  width: ${({ $width }) => $width}px;
  height: ${({ $width }) => $width}px;
  user-select: none;
  > span {
    line-height: ${({ $width }) => $width}px;
    font-size: ${({ $width }) => $width / 2 - 2}px;
    user-select: none;
  }
  ${({ $color, $backgroundColor, $setDefaultInitials, $isAuthUser }) =>
    $setDefaultInitials
      ? $isAuthUser
        ? `color: ${Color.PRIMARY}; background-color: #BADBFF;`
        : `color: ${Color.WHITE}; background-color: ${Color.PRIMARY};`
      : buildCssProps([
          ['color', $color && theme.getColor($color)],
          [
            'background-color',
            $backgroundColor && theme.getColor($backgroundColor),
          ],
        ])}
`;

const EmptyAvatar = styled(StyledAvatar)`
  /* neutral-5 */
  background-color: #d9d9d9;
`;

export default Avatar;
